import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const IDENTIFIERS = new Set(['email', 'user_id', 'phone']);

function defaultMessage(status) {
  if (status === 400) return 'That request was rejected.';
  if (status === 403) return 'Delete is turned off for this environment.';
  if (status === 404) return 'No account matches that search.';
  return 'Request failed.';
}

export function buildDeleteBody(field, value) {
  if (!IDENTIFIERS.has(field)) {
    throw new Error('Send exactly one of email, user id, or phone.');
  }
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    throw new Error('Enter an email, user id, or phone.');
  }
  if (field === 'user_id') {
    if (!/^\d+$/.test(trimmed)) {
      throw new Error('Enter a numeric user id.');
    }
    const userId = Number(trimmed);
    if (!Number.isSafeInteger(userId)) {
      throw new Error('Enter a numeric user id.');
    }
    return { user_id: userId, confirm: true };
  }
  return { [field]: trimmed, confirm: true };
}

export function buildLookupPath(field, value) {
  const body = buildDeleteBody(field, value);
  const identifier = field === 'user_id' ? String(body.user_id) : body[field];
  const params = new URLSearchParams();
  params.set(field, identifier);
  return `/admin/users/lookup?${params.toString()}`;
}

export function describeUsersError(error, action = 'Request') {
  const status = error?.status;
  const message = String(error?.message || '').trim();
  if (status === 403) {
    return message || 'Delete is turned off for this environment.';
  }
  if (status === 404) {
    return message || 'No account matches that search.';
  }
  if (status === 400) {
    if (/user[_\s-]?id/i.test(message)) return message;
    if (!message || /10|too many|more than/i.test(message)) {
      const lead = message || 'More than 10 accounts match.';
      return `${lead} Search by user id.`;
    }
    return message;
  }
  return message || `${action} failed.`;
}

async function request(path, { method = 'GET', body } = {}) {
  const token = getBearerToken();
  if (!token) {
    const error = new Error('Not authenticated');
    error.status = 401;
    throw error;
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: body == null ? undefined : JSON.stringify(body),
    });
  } catch {
    const error = new Error('Request failed.');
    error.status = 0;
    throw error;
  }

  const json = await response.json().catch(() => ({}));
  const statusCode = String(json.status_code ?? json.statusCode ?? '');
  if (!response.ok || statusCode !== '10000') {
    const error = new Error(json.message || defaultMessage(response.status));
    error.status = response.status;
    error.statusCode = statusCode;
    throw error;
  }
  return json.data ?? {};
}

export function fetchDeleteAccess() {
  return request('/admin/users/delete-access');
}

export function lookupUsers(field, value) {
  return request(buildLookupPath(field, value));
}

export function deleteUsers(field, value) {
  return request('/admin/users/delete', {
    method: 'POST',
    body: buildDeleteBody(field, value),
  });
}
